import React, { useEffect, useState } from "react";
import { Plus, X, Edit, Trash2, Upload, Download, Heart, ExternalLink, Copy, Mail, Check } from "lucide-react";
import api, { BASE } from "../api";
import { uploadUrl } from "../uploadUrl";
import { K } from "../theme";
import { Bg, Bt, Cd, Inp, Sl } from "./index";
import { notify, confirmDialog } from "../notify";
import { fmtDMY } from "../dateFormat";
import { usePermissions } from "../usePermissions";

// GHI (Group Health Insurance) — Employee-profile tab. Admin/HR can add
// dependents (parents/spouse/etc.) with a photo + ID document (e.g. Aadhar
// card); Centre Head and the employee themselves can view but not edit.
// The GHI Number is stored on employee_salary.ghi_number (same column the
// Salary & Payroll tab's "GHI Policy Number" field writes to — editing it
// from either place updates the same source of truth) and is also
// editable right here so admin/hr_head don't need to switch tabs for it.
//
// Linking dependents to an external enrollment portal (mentioned as a
// follow-up, SBI-related) is deliberately NOT built yet — out of scope
// for this pass.

const RELATIONSHIPS = ['Self', 'Spouse', 'Child', 'Parent', 'In-Law'];

const fmtDate = (d) => d ? fmtDMY(d) : '—';

export const EmployeeGhiTab = ({ employeeId, role, canPortal = false, canSendActivation = false }) => {
  const { can: pcan } = usePermissions(true);
  const canEdit = pcan('employees', 'ghi', null, 'edit');
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editingNumber, setEditingNumber] = useState(false);
  const [numberVal, setNumberVal] = useState('');
  const [savingNumber, setSavingNumber] = useState(false);
  // Catalogue comes from the server, not a second hardcoded copy here.
  const [grades, setGrades] = useState([]);
  const [savingGrade, setSavingGrade] = useState(false);
  // Portal link + username (no password — that comes from the Centre HR / Centre Head).
  const [editingPortal, setEditingPortal] = useState(false);
  const [portalForm, setPortalForm] = useState({ url: '', username: '' });
  const [savingPortal, setSavingPortal] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => api.get('/api/employees/' + employeeId + '/ghi-summary').then(setData).catch(() => setData(null));
  useEffect(() => { load(); }, [employeeId]); // eslint-disable-line
  useEffect(() => {
    api.get('/api/employees/ghi-grades')
      .then((d) => setGrades(Array.isArray(d) ? d : d.data || []))
      .catch(() => setGrades([]));
  }, []);

  const saveGrade = async (g) => {
    setSavingGrade(true);
    try {
      const r = await api.put(`/api/employees/${employeeId}/ghi-grade`, { grade: g || null });
      const res = r?.data || r || {};
      notify(
        res.grade
          ? `Grade ${res.grade} · cover ₹${Number(res.sum_insured).toLocaleString('en-IN')} · ₹${res.deduction_monthly}/month`
          : 'GHI grade cleared',
        { title: 'Saved', variant: 'success' }
      );
      load();
    } catch (e) { notify(e.message || 'Failed to set grade', { title: 'Error', variant: 'error' }); }
    finally { setSavingGrade(false); }
  };

  const gradeSpec = grades.find((g) => g.grade === data?.grade);

  const savePortal = async () => {
    setSavingPortal(true);
    try {
      await api.put(`/api/employees/${employeeId}/ghi-portal`, { portal_url: portalForm.url, username: portalForm.username });
      notify('GHI portal details saved', { title: 'Saved', variant: 'success' });
      setEditingPortal(false);
      load();
    } catch (e) { notify(e.message || 'Failed to save', { title: 'Error', variant: 'error' }); }
    finally { setSavingPortal(false); }
  };
  const copyText = (t) => { try { navigator.clipboard.writeText(t); notify('Copied', { title: 'Copied', variant: 'success' }); } catch (_) { /* clipboard unavailable */ } };
  const sendActivation = async () => {
    if (!(await confirmDialog('Email this employee their GHI details and tell them the GHI is active? They will be told to ask their Centre HR / Centre Head for the password. You will be copied.'))) return;
    setSending(true);
    try {
      const r = await api.post(`/api/employees/${employeeId}/ghi-activation-email`, {});
      if (r.sent) notify('GHI activation email sent', { title: 'Sent', variant: 'success' });
      else notify(r.reason === 'globally_disabled' || r.reason === 'smtp_disabled' ? 'Email sending is switched off on this server, so nothing was sent.' : `Email was not sent (${r.reason || 'unknown reason'}).`, { title: 'Not sent', variant: 'error' });
      load();
    } catch (e) { notify(e.message || 'Failed to send', { title: 'Error', variant: 'error' }); }
    finally { setSending(false); }
  };

  const saveNumber = async () => {
    setSavingNumber(true);
    try {
      await api.put(`/api/employees/${employeeId}/salary`, { ghi_number: numberVal.trim() || null });
      notify('GHI Number updated', { title: 'Saved', variant: 'success' });
      setEditingNumber(false);
      load();
    } catch (e) { notify(e.message || 'Failed to save', { title: 'Error', variant: 'error' }); }
    finally { setSavingNumber(false); }
  };

  const del = async (m) => {
    if (!(await confirmDialog(`Remove ${m.name} from the GHI family list?`))) return;
    setBusy(true);
    try { await api.del(`/api/employees/${employeeId}/ghi-family/${m.id}`); load(); notify('Removed', { title: 'Deleted', variant: 'success' }); }
    catch (e) { notify(e.message || 'Failed', { title: 'Error', variant: 'error' }); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="text-center p-6 text-t3 text-[14px]">Loading…</div>;

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[10px] mb-5">
        <Cd style={{ padding: 12 }}>
          <div className="text-[12px] text-t3 uppercase mb-[3px]">GHI Number</div>
          {editingNumber ? (
            <div className="flex items-center gap-1">
              <Inp value={numberVal} onChange={setNumberVal} placeholder="e.g. GHI-2026-00123" />
              <Bt s="xs" disabled={savingNumber} onClick={saveNumber}>{savingNumber ? '…' : 'Save'}</Bt>
              <Bt v="ghost" s="xs" onClick={() => setEditingNumber(false)}><X size={12} /></Bt>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="text-[15px] font-bold text-t0">{data.policyNumber || '—'}</div>
              {canEdit && <Bt v="ghost" s="xs" icon={Edit} onClick={() => { setNumberVal(data.policyNumber || ''); setEditingNumber(true); }} />}
            </div>
          )}
        </Cd>
        <Cd style={{ padding: 12 }}>
          <div className="text-[12px] text-t3 uppercase mb-[3px]">Status</div>
          <Bg c={data.status === 'Active' ? K.g : data.status === 'In Progress' ? K.a : K.t3} sm dot>{data.status}</Bg>
        </Cd>
        <Cd style={{ padding: 12 }}>
          <div className="text-[12px] text-t3 uppercase mb-[3px]">Sum Insured</div>
          <div className="text-[15px] font-bold text-t0">{data.sumInsured ? '₹' + data.sumInsured.toLocaleString('en-IN') : '—'}</div>
        </Cd>
        {/* Grade sets cover and deduction, so only admin/hr_head may change it. */}
        <Cd style={{ padding: 12 }}>
          <div className="text-[12px] text-t3 uppercase mb-[3px]">GHI Grade</div>
          {canEdit ? (
            <>
              <Sl
                options={[
                  { v: '', l: '— No grade —' },
                  ...grades.map((g) => ({
                    v: g.grade,
                    l: `${g.grade} · ${g.category} · ₹${g.sumInsured.toLocaleString('en-IN')}`,
                  })),
                ]}
                value={data.grade || ''}
                onChange={saveGrade}
                disabled={savingGrade}
              />
              <div className="text-[11px] text-t3 mt-[4px]">
                {savingGrade ? 'Saving…'
                  : gradeSpec ? `Deduction ₹${gradeSpec.deductionMonthly}/month`
                  : 'Sets cover and monthly deduction'}
              </div>
            </>
          ) : (
            <>
              <div className="text-[15px] font-bold text-t0">
                {data.grade ? `${data.grade} · ${data.gradeCategory || ''}` : '—'}
              </div>
              {gradeSpec && <div className="text-[11px] text-t3 mt-[4px]">Deduction ₹{gradeSpec.deductionMonthly}/month</div>}
            </>
          )}
        </Cd>
      </div>

      <Cd style={{ padding: 12, marginBottom: 20 }}>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="text-[14px] font-bold text-t0">GHI portal login</div>
          {canPortal && !editingPortal && <Bt v="ghost" s="xs" icon={Edit} onClick={() => { setPortalForm({ url: data.portalUrl || data.suggestedPortalUrl || '', username: data.username || '' }); setEditingPortal(true); }}>{data.portalUrl || data.username ? 'Edit' : 'Add'}</Bt>}
        </div>
        {editingPortal ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div><div className="text-[12px] text-t3 mb-[3px]">Portal link</div><Inp value={portalForm.url} onChange={v => setPortalForm(p => ({ ...p, url: v }))} placeholder="https://…" /></div>
            <div><div className="text-[12px] text-t3 mb-[3px]">Username</div><Inp value={portalForm.username} onChange={v => setPortalForm(p => ({ ...p, username: v }))} placeholder="Login username / ID" /></div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <Bt s="xs" disabled={savingPortal} onClick={savePortal}>{savingPortal ? 'Saving…' : 'Save'}</Bt>
              <Bt v="ghost" s="xs" onClick={() => setEditingPortal(false)}>Cancel</Bt>
              <span className="text-[12px] text-t3">The password is never stored here — the employee asks their Centre HR / Centre Head.</span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[12px] text-t3 uppercase mb-[3px]">Portal link</div>
              {data.portalUrl
                ? <a href={data.portalUrl} target="_blank" rel="noopener noreferrer" className="text-[14px] font-semibold flex items-center gap-1 break-all" style={{ color: K.bl }}>{data.portalUrl}<ExternalLink size={12} /></a>
                : <div className="text-[14px] text-t3">Not added yet</div>}
            </div>
            <div>
              <div className="text-[12px] text-t3 uppercase mb-[3px]">Username</div>
              {data.username
                ? <div className="flex items-center gap-2"><span className="text-[14px] font-bold text-t0 font-mono">{data.username}</span><Bt v="ghost" s="xs" icon={Copy} onClick={() => copyText(data.username)} /></div>
                : <div className="text-[14px] text-t3">Not added yet</div>}
            </div>
            <div className="sm:col-span-2 text-[12px] text-t3">For the password, contact your Centre HR or Centre Head.</div>
          </div>
        )}
        {canSendActivation && (() => {
          const ck = data.activationChecklist || {};
          const items = [['GHI number', ck.policyNumber], ['Portal link', ck.portalUrl], ['Username', ck.username]];   // family members are optional
          const ready = items.every(([, ok]) => ok);
          return (
            <div className="mt-3 pt-3 border-t border-b0 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[12px]">
                  {items.map(([l, ok]) => <span key={l} className="flex items-center gap-[3px]" style={{ color: ok ? K.g : K.t3 }}>{ok ? <Check size={12} /> : <X size={12} />}{l}</span>)}
                </div>
                <div className="text-[12px] text-t3 mt-1">Family members are optional — if some are added, they are listed in the email.</div>
                {data.activationSentAt && <div className="text-[12px] text-t3 mt-1">Last emailed {fmtDate(data.activationSentAt)}{data.activationSentBy ? ' by ' + data.activationSentBy : ''}</div>}
              </div>
              <Bt icon={Mail} disabled={!ready || sending} onClick={sendActivation}>{sending ? 'Sending…' : data.activationSentAt ? 'Send again' : 'Send GHI activation email'}</Bt>
            </div>
          );
        })()}
      </Cd>

      <div className="flex items-center justify-between mb-3">
        <div className="text-[14px] font-bold text-t0 flex items-center gap-2"><Heart size={13} color={K.r} /> Family Members</div>
        {canEdit && <Bt icon={Plus} s="sm" onClick={() => { setEditing(null); setShowForm(true); }}>Add Member</Bt>}
      </div>

      {(!data.family || data.family.length === 0) ? (
        <div className="text-center p-8 text-t3 text-[14px] bg-bg2 rounded-lg border border-b0">No family members added yet.</div>
      ) : (
        <div className="grid gap-2">
          {data.family.map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-bg2 border border-b0 flex-wrap">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {m.photo_path ? (
                  <img src={uploadUrl(m.photo_path)} alt={m.name} className="w-10 h-10 rounded-full object-cover border border-b0" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-bg1 border border-b0 flex items-center justify-center text-[14px] font-bold text-t3">{(m.name || '?')[0]}</div>
                )}
                <div className="min-w-0">
                  <div className="text-[14px] font-bold text-t0 truncate">{m.name}</div>
                  <div className="text-[12px] text-t3">{m.relationship}{m.dob ? ' · ' + fmtDate(m.dob) : ''}{m.gender ? ' · ' + m.gender : ''}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {m.document_path && <Bt v="ghost" s="xs" icon={Download} onClick={() => window.open(uploadUrl(m.document_path), '_blank')}>Document</Bt>}
                {canEdit && (
                  <>
                    <Bt v="ghost" s="xs" icon={Edit} onClick={() => { setEditing(m); setShowForm(true); }} />
                    <Bt v="danger" s="xs" icon={Trash2} disabled={busy} onClick={() => del(m)} />
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!canEdit && <div className="mt-3 text-[14px] text-t3">Contact HR to add or update GHI family members.</div>}

      {showForm && (
        <GhiFamilyDialog
          employeeId={employeeId}
          member={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
};

const GhiFamilyDialog = ({ employeeId, member, onClose, onSaved }) => {
  const [name, setName] = useState(member?.name || '');
  const [relationship, setRelationship] = useState(member?.relationship || 'Spouse');
  const [dob, setDob] = useState((member?.dob || '').slice(0, 10));
  const [gender, setGender] = useState(member?.gender || '');
  const [photoFile, setPhotoFile] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const uploadFile = async (file, docType) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('employee_id', employeeId);
    fd.append('doc_type', docType);
    const res = await fetch(BASE + '/api/documents/upload', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + localStorage.getItem('hrms_token') },
      body: fd,
    });
    const data = await res.json();
    if (!data.file_path) throw new Error('Upload failed');
    return data.file_path;
  };

  const save = async () => {
    if (!name.trim()) { notify('Name is required', { title: 'Error', variant: 'error' }); return; }
    setSaving(true);
    try {
      let photoPath = member?.photo_path || null;
      let docPath = member?.document_path || null;
      if (photoFile) photoPath = await uploadFile(photoFile, 'GHI Dependent Photo');
      if (docFile) docPath = await uploadFile(docFile, 'GHI Dependent Document');
      const payload = { name: name.trim(), relationship, dob: dob || null, gender: gender || null, photo_path: photoPath, document_path: docPath };
      if (member?.id) await api.put(`/api/employees/${employeeId}/ghi-family/${member.id}`, payload);
      else await api.post(`/api/employees/${employeeId}/ghi-family`, payload);
      notify(member?.id ? 'Family member updated' : 'Family member added', { title: 'Saved', variant: 'success' });
      onSaved();
    } catch (e) { notify(e.message || 'Failed to save', { title: 'Error', variant: 'error' }); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[440px] max-h-[90vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <span className="text-[16px] font-extrabold text-t0">{member?.id ? 'Edit' : 'Add'} Member</span>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
        </div>
        <div className="space-y-[10px]">
          <div><div className="text-[14px] text-t2 mb-1">Name *</div><Inp value={name} onChange={setName} placeholder="e.g. Sunita Sharma" /></div>
          <div><div className="text-[14px] text-t2 mb-1">Relationship *</div><Sl options={RELATIONSHIPS.map((r) => ({ v: r, l: r }))} value={relationship} onChange={setRelationship} /></div>
          <div className="grid grid-cols-2 gap-[10px]">
            <div><div className="text-[14px] text-t2 mb-1">Date of Birth</div><input type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="w-full px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none" /></div>
            <div><div className="text-[14px] text-t2 mb-1">Gender</div><Sl options={[{ v: '', l: 'Select' }, { v: 'M', l: 'Male' }, { v: 'F', l: 'Female' }, { v: 'O', l: 'Other' }]} value={gender} onChange={setGender} /></div>
          </div>
          <div>
            <div className="text-[14px] text-t2 mb-1">Photo</div>
            {member?.photo_path && !photoFile && <div className="text-[12px] text-t2 mb-1">Current: {member.photo_path}</div>}
            <label className="flex items-center gap-2 px-3 py-2 rounded-[10px] border-2 border-dashed border-b1 bg-bg2 cursor-pointer text-t2 text-[14px]">
              <Upload size={12} color={K.p} />{photoFile ? photoFile.name : 'Choose photo'}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files[0]; if (f) setPhotoFile(f); }} />
            </label>
          </div>
          <div>
            <div className="text-[14px] text-t2 mb-1">Document (e.g. Aadhar Card)</div>
            {member?.document_path && !docFile && <div className="text-[12px] text-t2 mb-1">Current: {member.document_path}</div>}
            <label className="flex items-center gap-2 px-3 py-2 rounded-[10px] border-2 border-dashed border-b1 bg-bg2 cursor-pointer text-t2 text-[14px]">
              <Upload size={12} color={K.p} />{docFile ? docFile.name : 'Choose PDF / image'}
              <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => { const f = e.target.files[0]; if (f) setDocFile(f); }} />
            </label>
          </div>
        </div>
        <div className="flex gap-[6px] mt-4">
          <Bt full disabled={saving} onClick={save}>{saving ? 'Saving…' : (member?.id ? 'Update' : 'Save')}</Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};
